import React, { Component, ChangeEvent } from "react";
import { inject, observer } from "mobx-react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { Utils } from "@/common/utils";
import { AttachmentFile } from "Components/Common/AttachmentSelector";
import { TicketStore, AttachmentStore, SnapshotStore } from "../../stores";

interface SnapshotConfirmModalProps {
    show: boolean;
    onCancel: () => void;
    onWithSnapshot: () => void;
    onWithoutSnapshot: () => void;
}

export const SnapshotConfirmModal = ({ show, onCancel, onWithSnapshot, onWithoutSnapshot }: SnapshotConfirmModalProps) => {
    return (
        <Modal show={show} onHide={onCancel} centered>
            <Modal.Body>
                <div className="header edit">CLOSE ACTIONPLAN</div>
                <div style={{ fontSize: 16, paddingBottom: 20 }}>
                    Do you want to attach a current snapshot of the dashboard before closing?
                </div>
                <div className="text-end">
                    <Button
                        variant="outline-primary"
                        size="lg"
                        onClick={onWithoutSnapshot}
                        style={{ marginRight: 15 }}
                    >
                        No
                    </Button>
                    <Button
                        variant="primary"
                        size="lg"
                        onClick={onWithSnapshot}
                    >
                        Yes
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

interface ActionplanCloseModalProps {
    ticketStore?: TicketStore;
    attachmentStore?: AttachmentStore;
    snapshotStore?: SnapshotStore;
    show: boolean;
    onCancel: () => void;
    onSave: (finalComment: string, goalMet: boolean, attachmentId?: string) => void;
    isQuickActionplan: boolean;
    isSaving: boolean;
}

interface ActionplanCloseModalState {
    finalComment: string;
    goalMet: undefined | string;
    attachment: undefined | AttachmentFile;
}

@inject("ticketStore", "attachmentStore", "snapshotStore")
@observer
export class ActionplanCloseModal extends Component<ActionplanCloseModalProps, ActionplanCloseModalState> {
    filesRef: HTMLInputElement | null = null;

    constructor(props: ActionplanCloseModalProps) {
        super(props);

        this.state = {
            finalComment: "",
            goalMet: undefined,
            attachment: undefined,
        };
    }

    handleCommentChange = (ev: ChangeEvent<HTMLInputElement>) => {
        this.setState({ finalComment: ev.target.value });
    };

    handleGoalMetChange = (ev: ChangeEvent<HTMLInputElement>) => {
        this.setState({ goalMet: ev.target.value });
    };

    handleAttachmentChange = (ev: ChangeEvent<HTMLInputElement>) => {
        ev.preventDefault();
        const file = ev.target.files?.[0];
        if (file) {
            const attachment = file as AttachmentFile;
            attachment.sanitizedName = Utils.sanitizeFileName(file.name);
            this.setState({ attachment });
        }
    };

    handleAttachmentClear = () => {
        this.setState({ attachment: undefined });
    };

    handleCloseActionplan = async () => {
        const goalMet = this.state.goalMet!.toLowerCase() === "true";

        try {
            if (this.state.attachment !== undefined) {
                const resp = await this.props.attachmentStore!.createAndUploadAttachments([this.state.attachment!]);
                this.props.onSave(this.state.finalComment, goalMet, resp[0]);
            } else {
                this.props.onSave(this.state.finalComment, goalMet);
            }
        } catch (error) {
            console.error("Error closing actionplan:", error);
        }
    };

    showAttachmentSelector = () => {
        this.filesRef?.click();
    };

    assignFileRef = (ref: HTMLInputElement | null) => {
        this.filesRef = ref;
    };

    render() {
        if (!this.props.show) return null;

        return (
            <Modal show={this.props.show} onHide={this.props.onCancel} centered>
                <Modal.Body>
                    <div className="header edit">CLOSE ACTIONPLAN</div>
                    <div style={{ fontSize: 16, paddingBottom: 10 }}>
                        Before you close this ActionPlan, please leave a comment and verify if target was met.
                    </div>
                    <div className="target-goal" id="ticketStatusUpdate">
                        <Form.Group>
                            <Form.Label>Final Comment</Form.Label>
                            <Form.Control
                                as="textarea"
                                onChange={this.handleCommentChange}
                                value={this.state.finalComment}
                            />
                        </Form.Group>

                        <div className="closing-attachment-selector">
                            <span className="link" onClick={this.showAttachmentSelector}>Upload an image</span>
                            &nbsp;{this.props.isQuickActionplan
                                ? "to use for the final snapshot (optional)"
                                : "to use in place of a dashboard snapshot (optional)"}
                            <input
                                style={{ display: "none" }}
                                type="file"
                                accept="image/*"
                                onChange={this.handleAttachmentChange}
                                ref={this.assignFileRef}
                            />
                            {this.state.attachment === undefined ? null :
                                <div className="item-chip">
                                    {this.state.attachment.sanitizedName}
                                    <i className="icon icon-times" onClick={this.handleAttachmentClear} />
                                </div>
                            }
                        </div>

                        <div>
                            <p>Was the measurable Target (Goal) Achieved?</p>
                            <Form.Check
                                inline
                                label="yes"
                                value="true"
                                checked={this.state.goalMet === "true"}
                                name="measurable-target-goal-met-radio"
                                type="radio"
                                onChange={this.handleGoalMetChange}
                            />
                            <Form.Check
                                inline
                                label="no"
                                value="false"
                                checked={this.state.goalMet === "false"}
                                name="measurable-target-goal-met-radio"
                                onChange={this.handleGoalMetChange}
                                type="radio"
                            />
                        </div>

                        <div className="text-end">
                            <Button
                                variant="secondary"
                                size="lg"
                                onClick={this.props.onCancel}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                size="lg"
                                style={{ marginLeft: 15 }}
                                disabled={!this.state.finalComment || this.state.finalComment === "" || this.state.goalMet === undefined}
                                onClick={this.handleCloseActionplan}
                            >
                                {this.props.ticketStore!.isProcessing || this.props.attachmentStore!.isUploading || this.props.isSaving ?
                                    <Spinner animation="border" /> : "Save"}
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        );
    }
}